import React, { Dispatch } from "react";

import { $Logger } from "../../../../logger";

import type {
	IChatBoxListUIController,
	ChatBoxListState,
} from "./chat-box-list-ui-controller.interface";
import { ChatBoxListScroller, IChatBoxListScroller } from "../chat-box-list-scroller";
import {
	IChatBoxListAnimationEngine,
	ChatBoxListAnimationEngine,
	AnimationScopeId,
} from "../../engines";

import {
	isStickerMessageContent,
	isGroupStickerMessageContent,
	IUIGroupStickerMessageContent,
} from "../../../../ui/shared/types";

import type {
	ChatBoxListActionType,
	InitChatBoxListActionType,
	AddNewStickerMessageActionType,
	SendNewTextMessageActionType,
	DeleteMessagesByCliMsgIdsActionType,
	ReceiveNewMessageActionType,
	ClearUnreadActionType,
	ReadLastMessageActionType,
} from "./chat-box-list-ui-controller.actions";
import type { FlipperAnimationScope } from "../../engines/flip-animation-setup-flow/flip-animation-setup-flow.interface";

import { messageAppService } from "../../../../app/message";
import { ITextMessageDTO, IStickerMessageDTO, IMessageDTO } from "../../../../shared/dtos";
import { UnbindUIView } from "../types";

const MAX_GROUP_STICKER_MESSAGE_COUNT = 3;

const DefaultChatBoxListState: () => ChatBoxListState = () => ({
	animationScopeId: 0,
	messages: [],
	lastUnreadCliMsgId: "",
	unreadCount: 0,
});

//TODO: we should import from dispatch from somewhere.
const NoopUIEventDispatcher = (...args: any[]) => {
	$Logger.error(
		`[WARNING][ChatBoxListUIController] We don't have a real dispatcher to dispatch actions.`,
		args,
	);
	$Logger.error(
		`[WARNING][ChatBoxListUIController] Maybe you perform a wrong binding of UI Event Dispatcher.`,
	);
};

export class ChatBoxListUIController implements IChatBoxListUIController {
	private convId_: string;
	private UIEventDispatcher_: Dispatch<any>;
	private currentAnimationScopeId_: number;
	private state_: ChatBoxListState;
	private scroller_: IChatBoxListScroller;
	private animationNG_: IChatBoxListAnimationEngine;
	private chatBoxListId_: string | undefined;

	public constructor(convId: string, initialState?: ChatBoxListState) {
		this.state_ = initialState ?? DefaultChatBoxListState();
		this.scroller_ = new ChatBoxListScroller();
		this.animationNG_ = new ChatBoxListAnimationEngine();
		this.currentAnimationScopeId_ = this.animationNG_.currentAnimationScopeId;
		this.UIEventDispatcher_ = NoopUIEventDispatcher;
		this.convId_ = convId;
		this.chatBoxListId_ = `#cbl-${this.convId_}`;
	}

	public bindUIView(): UnbindUIView {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [state, dispatch] = React.useReducer(
			this.ChatBoxListStateReducer_,
			// eslint-disable-next-line react-hooks/rules-of-hooks
			React.useMemo(DefaultChatBoxListState, []),
		);
		this.state_ = state;
		this.UIEventDispatcher_ = dispatch;
		return this.dispose;
	}

	private ChatBoxListStateReducer_(state: ChatBoxListState, action: ChatBoxListActionType) {
		let newState = state;
		switch (action.type) {
			case "ChatBoxListAction.InitChatBoxList": {
				const { messages, override } = action.payload;
				if (override) {
					newState = {
						...state,
						messages,
					};
					break;
				}

				//TODO: we should merge messages with the existing messages...
				newState = {
					...state,
					messages,
				};
				break;
			}

			case "ChatBoxListAction.SendNewTextMessage": {
				const { message, status, shouldPlayAnimation } = action.payload;
				//@WARNING: If we do that maybe we can make a race condition here, because animationScopeId from payload.
				//Maybe, smaller than current state.animationScopeId.
				const animationScopeId = action.payload.animationScopeId ?? state.animationScopeId;
				newState = {
					...state,
					animationScopeId,
					messages: [...state.messages, message],
				};
				break;
			}

			case "ChatBoxListAction.AddNewStickerMessage": {
				const { message, status, shouldPlayAnimation } = action.payload;
				//@WARNING: If we do that maybe we can make a race condition here, because animationScopeId from payload.
				//Maybe, smaller than current state.animationScopeId.
				const animationScopeId = action.payload.animationScopeId ?? state.animationScopeId;
				const currentMessages = [...state.messages];
				const lastMessage = currentMessages[currentMessages.length - 1];
				if (lastMessage && isStickerMessageContent(lastMessage)) {
					const groupStickerMessageContent: IUIGroupStickerMessageContent = {
						...lastMessage,
						type: "group-sticker",
						content: [lastMessage, message],
					};
					currentMessages[currentMessages.length - 1] = groupStickerMessageContent;
					newState = {
						...state,
						animationScopeId,
						messages: currentMessages,
					};
					break;
				} else if (
					lastMessage &&
					isGroupStickerMessageContent(lastMessage) &&
					lastMessage.content.length < MAX_GROUP_STICKER_MESSAGE_COUNT
				) {
					const newLastMessage = {
						...lastMessage,
						content: [...lastMessage.content, message],
					};
					currentMessages[currentMessages.length - 1] = newLastMessage;
					newState = {
						...state,
						animationScopeId,
						messages: currentMessages,
					};
					break;
				} else {
					const groupStickerMessageContent: IUIGroupStickerMessageContent = {
						...message,
						type: "group-sticker",
						content: [message],
					};
					newState = {
						...state,
						animationScopeId,
						messages: [...state.messages, groupStickerMessageContent],
					};
					break;
				}
			}

			case "ChatBoxListAction.DeleteMessagesByCliMsgIds": {
				const { cliMsgIds } = action.payload;
				//@TODO: build a again chat box list UI view model.
				newState = {
					...state,
					messages: [
						...state.messages.filter(
							(message) => !cliMsgIds.includes(message.cliMsgId),
						),
					],
				};
				break;
			}
			case "ChatBoxListAction.ReceiveNewMessage": {
				const { message, status, shouldPlayAnimation } = action.payload;
				const newMsgCount = message.cliMsgId > state.lastUnreadCliMsgId ? 1 : 0;
				newState = {
					...state,
					messages: [...state.messages, message],
					lastUnreadCliMsgId: newMsgCount ? message.cliMsgId : state.lastUnreadCliMsgId,
					unreadCount: state.unreadCount + newMsgCount,
				};
				break;
			}
			case "ChatBoxListAction.ClearUnread": {
				newState = {
					...state,
					unreadCount: 0,
				};
				break;
			}
			case "ChatBoxListAction.ReadLastMessage": {
				const { message: lastMessage } = action.payload;
				const readOverLastUnreadCliMsgId = lastMessage.cliMsgId >= state.lastUnreadCliMsgId;
				newState = {
					...state,
					unreadCount: readOverLastUnreadCliMsgId ? 0 : state.unreadCount,
					lastUnreadCliMsgId: readOverLastUnreadCliMsgId
						? lastMessage.cliMsgId
						: state.lastUnreadCliMsgId,
				};
				break;
			}
			default: {
				break;
			}
		}

		// this.state_ = newState;
		return newState;
	}

	public get scroller(): IChatBoxListScroller {
		return this.scroller_;
	}

	public get animationNG(): IChatBoxListAnimationEngine {
		return this.animationNG_;
	}

	public get state(): ChatBoxListState {
		return this.state_;
	}

	public get animationScopeId(): number {
		return this.currentAnimationScopeId_;
	}

	public get chatBoxListId(): string | undefined {
		return this.chatBoxListId_;
	}

	public async initChatBoxList(opts?: any): Promise<void> {
		const messages = await messageAppService.loadMessages(this.convId_, opts);
		const action: InitChatBoxListActionType = {
			type: "ChatBoxListAction.InitChatBoxList",
			payload: {
				messages,
				override: true,
			},
		};
		this.UIEventDispatcher_(action);
	}

	public async sendNewTextMessage(
		content: {
			toUid: string;
			fromUid: string;
			content: string;
		},
		extra?: { shouldPlayAnimation?: boolean },
	): Promise<void> {
		const { shouldPlayAnimation = true } = extra ?? {};

		const localDttm = Date.now();
		const newTextMessageDTO: ITextMessageDTO = await messageAppService.sendNewTextMessage({
			toUid: content.toUid,
			fromUid: content.fromUid,
			content: content.content,
			localDttm,
			sendDttm: localDttm,
		});
		const action: SendNewTextMessageActionType = {
			type: "ChatBoxListAction.SendNewTextMessage",
			payload: {
				status: "new",
				shouldPlayAnimation,
				message: newTextMessageDTO,
			},
		};
		this.UIEventDispatcher_(action);
		if (shouldPlayAnimation) {
			let scopeId = this.animationNG.markANewAnimationScope(
				{
					flow: "add-new-text-message",
					startTime: Date.now(),
					scopeData: {},
				},
				{
					action,
					state: this.state,
				},
			);
			this.currentAnimationScopeId_ = scopeId;
			action.payload.animationScopeId = scopeId;
		}
	}

	public async addNewStickerMessage(
		content: {
			toUid: string;
			fromUid: string;
			content: {
				id: number;
				cateId: number;
				type: number;
				text: string;
				uri: string;
				fkey: number;
				status: number;
				stickerUrl: string;
				stickerSpriteUrl: string;
				stickerWebpUrl: string | null;
				totalFrames: number;
				duration: number;
				effectId: number;
				checksum: string | null;
				ext: number;
				source: number;
				fss: any | null;
				fssInfo: any | null;
				version: number;
				extInfo: any | null;
				fetchTs: number;
				updateState: number;
			};
		},
		extra?: { shouldPlayAnimation?: boolean; stickerDOMRectSnapshot?: DOMRect },
	): Promise<void> {
		const { shouldPlayAnimation = true, stickerDOMRectSnapshot } = extra ?? {};
		const localDttm = Date.now();
		const newStickerMessageDTO: IStickerMessageDTO =
			await messageAppService.sendNewStickerMessage({
				toUid: content.toUid,
				fromUid: content.fromUid,
				content: content.content,
				localDttm: localDttm,
				sendDttm: localDttm,
			});

		const action: AddNewStickerMessageActionType = {
			type: "ChatBoxListAction.AddNewStickerMessage",
			payload: {
				status: "new",
				shouldPlayAnimation,
				message: newStickerMessageDTO,
			},
		};
		if (shouldPlayAnimation) {
			const scopeId = this.animationNG.markANewAnimationScope(
				{
					flow: "add-new-sticker-message",
					startTime: Date.now(),
					scopeData: {
						stickerDOMRectSnapshot,
					},
				},
				{
					action,
					state: this.state,
					stickerDOMRectSnapshot,
				},
			);
			this.currentAnimationScopeId_ = scopeId;
			action.payload.animationScopeId = scopeId;

			const flipperAnimationScope: FlipperAnimationScope<"sequential"> = {
				mode: "sequential",
				cancelPreviousOne: false,
				decisionData: {
					previous: this.state,
					current: this.state,
				},
				flippedAnimations: [
					{
						flippedId: newStickerMessageDTO.cliMsgId,
						onStart: (element, decisionData) => {
							console.log("dongmt onStart", element, decisionData);
						},
						onStartImmediate: (element, decisionData) => {
							console.log("dongmt onStartImmediate", element, decisionData);
							//apply snapshot to element here...
						},
						onComplete: (element, decisionData) => {
							console.log("dongmt onComplete", element, decisionData);
						},
						onAppear: (element, index, decisionData) => {
							console.log("dongmt onAppear", element, index, decisionData);
						},
						onExit: (element, index, removeElement, decisionData) => {
							console.log(
								"dongmt onExit",
								element,
								index,
								removeElement,
								decisionData,
							);
						},
					},
				],
			};
			const flipperAnimatioScopeId =
				this.animationNG_.applyNewFlipperAnimationScope(flipperAnimationScope);
		}
		this.UIEventDispatcher_(action);
	}

	public loadMoreMessages(opts?: any): Promise<void> {
		return Promise.resolve();
	}

	public async deleteMessagesByCliMsgIds(cliMsgIds: string[]): Promise<void> {
		await messageAppService.deleteMessagesByCliMsgIds(this.convId_, cliMsgIds);
		const action: DeleteMessagesByCliMsgIdsActionType = {
			type: "ChatBoxListAction.DeleteMessagesByCliMsgIds",
			payload: {
				cliMsgIds,
			},
		};
		this.UIEventDispatcher_(action);
	}

	public readLastMessages(message: IMessageDTO): void {
		const action: ReadLastMessageActionType = {
			type: "ChatBoxListAction.ReadLastMessage",
			payload: {
				message,
			},
		};
		this.UIEventDispatcher_(action);
	}

	public clearUnread(): void {
		const action: ClearUnreadActionType = {
			type: "ChatBoxListAction.ClearUnread",
			payload: {},
		};
		this.UIEventDispatcher_(action);
	}

	public onReceiveNewMessage(message: IMessageDTO): void {
		if (message.fromUid !== this.convId_) return;

		const action: ReceiveNewMessageActionType = {
			type: "ChatBoxListAction.ReceiveNewMessage",
			payload: {
				status: "receive",
				message,
			},
		};
		this.UIEventDispatcher_(action);
	}

	public dispose(): void {
		console.log("dongmt CBL dispose");
		this.UIEventDispatcher_ = NoopUIEventDispatcher;
	}
}
